import { ThemeEvents } from '@theme/events';

class CustomSubscription extends HTMLElement {
  constructor() {
    super();

    this.initialized = false;
    this.handleChange = this.handleSelectionChange.bind(this);
    this.handleVariantUpdate = this.onVariantUpdate.bind(this);
    this.handleMilestoneOpen = this.openMilestoneModal.bind(this);
    this.handleMilestoneClose = this.closeMilestoneModal.bind(this);
    this.handleMilestoneBackdrop = this.onMilestoneBackdropClick.bind(this);
    this.handleMilestoneKeydown = this.onMilestoneKeydown.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.init();
    this.attachVariantListener();
    this.attachMilestoneModal();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleChange);
    this.eventTarget?.removeEventListener(
      ThemeEvents.variantUpdate,
      this.handleVariantUpdate
    );
    this.detachMilestoneModal();
  }

  init() {
    this.form = this.findForm();
    if (!this.form) return;

    if (!this.form.getAttribute('id')) {
      this.form.setAttribute(
        'id',
        `product-form-${Math.random().toString(36).slice(2, 9)}`
      );
    }

    this.sellingPlanIdInput = this.querySelector('input[name="selling_plan"]');
    if (!this.sellingPlanIdInput) return;

    this.sellingPlanIdInput.setAttribute('form', this.form.getAttribute('id'));
    this.variantWrappers = Array.from(
      this.querySelectorAll('.custom-subscription-variant')
    );

    this.addEventListener('change', this.handleChange);

    const initialVariantId =
      this.form.querySelector('input[name="id"]')?.value ||
      this.form.querySelector('select[name="id"]')?.value;

    if (initialVariantId) {
      this.setActiveWrapper(initialVariantId);
    }

    this.syncVisibility();
    this.syncSelectionFromActiveWrapper();
    this.syncWidgetPriceFromSelection();
    this.syncAtcPriceFromSelection();
  }

  findForm() {
    const directForm = this.closest('product-form-component')?.querySelector('form');
    if (directForm) return directForm;

    const scopes = [
      this.closest('.product-details'),
      this.closest('dialog'),
      this.closest('product-card'),
      this.closest('.shopify-section'),
    ].filter(Boolean);

    for (const scope of scopes) {
      const form = scope.querySelector('product-form-component form');
      if (form) return form;
    }

    return null;
  }

  attachVariantListener() {
    if (!this.sellingPlanIdInput) return;

    this.eventTarget =
      this.closest('dialog') ||
      this.closest('product-card') ||
      this.closest('.shopify-section') ||
      this.closest('.product-details');

    this.eventTarget?.addEventListener(
      ThemeEvents.variantUpdate,
      this.handleVariantUpdate
    );
  }

  attachMilestoneModal() {
    this.milestoneModal = this.querySelector('[data-bf-milestone-modal]');
    if (!this.milestoneModal) return;

    const modalId = this.milestoneModal.getAttribute('id');
    this.milestoneClose = this.milestoneModal.querySelector(
      '[data-bf-milestone-close]'
    );
    const externalTriggerScope =
      this.closest('.product-details') ||
      this.closest('.shopify-section') ||
      document;

    if (this.milestoneModal.parentElement !== document.body) {
      document.body.appendChild(this.milestoneModal);
    }

    this.milestoneTriggers = Array.from(
      new Set([
        ...this.querySelectorAll('[data-bf-milestone-trigger]'),
        ...externalTriggerScope.querySelectorAll(
          '[data-bf-milestone-external-trigger]'
        ),
      ])
    );

    this.milestoneTriggers.forEach((trigger) => {
      if (modalId) {
        trigger.setAttribute('aria-controls', modalId);
      }
      trigger.addEventListener('click', this.handleMilestoneOpen);
    });
    this.milestoneClose?.addEventListener('click', this.handleMilestoneClose);
    this.milestoneModal.addEventListener('click', this.handleMilestoneBackdrop);
    document.addEventListener('keydown', this.handleMilestoneKeydown);
  }

  detachMilestoneModal() {
    this.milestoneTriggers?.forEach((trigger) => {
      trigger.removeEventListener('click', this.handleMilestoneOpen);
    });
    this.milestoneClose?.removeEventListener('click', this.handleMilestoneClose);
    this.milestoneModal?.removeEventListener('click', this.handleMilestoneBackdrop);
    document.body.classList.remove('bf-milestone-modal-open');
    document.removeEventListener('keydown', this.handleMilestoneKeydown);
  }

  openMilestoneModal(event) {
    event?.preventDefault();
    if (!this.milestoneModal) return;

    this.lastMilestoneFocus = document.activeElement;
    this.milestoneModal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bf-milestone-modal-open');
    this.milestoneClose?.focus();
  }

  closeMilestoneModal() {
    if (!this.milestoneModal) return;

    this.milestoneModal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bf-milestone-modal-open');

    if (this.lastMilestoneFocus && typeof this.lastMilestoneFocus.focus === 'function') {
      this.lastMilestoneFocus.focus();
    }
  }

  onMilestoneBackdropClick(event) {
    if (event.target === this.milestoneModal) {
      this.closeMilestoneModal();
    }
  }

  onMilestoneKeydown(event) {
    if (
      event.key === 'Escape' &&
      this.milestoneModal?.getAttribute('aria-hidden') === 'false'
    ) {
      this.closeMilestoneModal();
    }
  }

  getActiveWrapper() {
    return this.querySelector('.custom-subscription-variant.active');
  }

  setActiveWrapper(variantId) {
    let activeWrapper = null;

    this.variantWrappers.forEach((wrapper) => {
      const isActive = String(wrapper.dataset.id) === String(variantId);
      wrapper.classList.toggle('active', isActive);

      if (isActive) {
        activeWrapper = wrapper;
      }
    });

    return activeWrapper;
  }

  syncVisibility() {
    this.hidden = !this.getActiveWrapper();
  }

  syncSelectionFromActiveWrapper() {
    if (!this.sellingPlanIdInput) return;

    const activeWrapper = this.getActiveWrapper();
    if (!activeWrapper) {
      this.sellingPlanIdInput.value = '';
      return;
    }

    let selectedOptionEl = activeWrapper.querySelector(
      '.custom-subscription__option_input:checked'
    );

    if (!selectedOptionEl) {
      selectedOptionEl =
        activeWrapper.querySelector('.custom-subscription__option_input[data-default="true"]') ||
        activeWrapper.querySelector('.custom-subscription__option_input');

      if (selectedOptionEl) {
        selectedOptionEl.checked = true;
      }
    }

    if (!selectedOptionEl || selectedOptionEl.value === 'onetime') {
      this.sellingPlanIdInput.value = '';
      return;
    }

    let selectedFrequencyEl = activeWrapper.querySelector(
      '.custom-subscription__frequency-input:checked'
    );

    if (!selectedFrequencyEl) {
      selectedFrequencyEl =
        activeWrapper.querySelector('.custom-subscription__frequency-input[data-default="true"]') ||
        activeWrapper.querySelector('.custom-subscription__frequency-input');

      if (selectedFrequencyEl) {
        selectedFrequencyEl.checked = true;
      }
    }

    this.sellingPlanIdInput.value = selectedFrequencyEl?.value || '';

    if (selectedFrequencyEl) {
      selectedOptionEl.dataset.price = selectedFrequencyEl.dataset.price || '';

      if (selectedFrequencyEl.dataset.comparePrice) {
        selectedOptionEl.dataset.comparePrice = selectedFrequencyEl.dataset.comparePrice;
      } else {
        delete selectedOptionEl.dataset.comparePrice;
      }
    }
  }

  getAtcPriceElements() {
    const addToCartButton = this.form?.querySelector('[ref="addToCartButton"]');

    return {
      current: addToCartButton?.querySelector('.add-to-cart-price__current'),
      compare: addToCartButton?.querySelector('.add-to-cart-price__compare'),
    };
  }

  getWidgetPriceElements(activeWrapper) {
    const subscribeLabel = activeWrapper?.querySelector('.custom-subscription__subscribe');

    return {
      current: subscribeLabel?.querySelector('.custom-subscription__final-price'),
      compare: subscribeLabel?.querySelector('.custom-subscription__original-price'),
    };
  }

  syncWidgetPriceFromSelection() {
    const activeWrapper = this.getActiveWrapper();
    if (!activeWrapper) return;

    const subscribeOptionEl = activeWrapper.querySelector(
      '.custom-subscription__option_input[value="subscribe"]'
    );
    const selectedFrequencyEl = activeWrapper.querySelector(
      '.custom-subscription__frequency-input:checked'
    );

    if (!subscribeOptionEl || !selectedFrequencyEl) return;

    const currentPrice = selectedFrequencyEl.dataset.price || subscribeOptionEl.dataset.price || '';
    const comparePrice =
      selectedFrequencyEl.dataset.comparePrice || subscribeOptionEl.dataset.comparePrice || '';

    const { current, compare } = this.getWidgetPriceElements(activeWrapper);

    if (current && currentPrice) {
      current.textContent = currentPrice;
    }

    if (compare) {
      if (comparePrice) {
        compare.textContent = comparePrice;
        compare.hidden = false;
      } else {
        compare.textContent = '';
        compare.hidden = true;
      }
    }
  }

  syncAtcPriceFromSelection() {
    const activeWrapper = this.getActiveWrapper();
    if (!activeWrapper || this.hidden) return;

    const selectedOptionEl = activeWrapper.querySelector(
      '.custom-subscription__option_input:checked'
    );
    const selectedFrequencyEl = activeWrapper.querySelector(
      '.custom-subscription__frequency-input:checked'
    );

    if (!selectedOptionEl) return;

    let price = selectedOptionEl.dataset.price || '';
    let comparePrice = selectedOptionEl.dataset.comparePrice || '';

    if (selectedOptionEl.value === 'subscribe' && selectedFrequencyEl) {
      price = selectedFrequencyEl.dataset.price || price;
      comparePrice = selectedFrequencyEl.dataset.comparePrice || comparePrice;
    }

    const { current, compare } = this.getAtcPriceElements();

    if (current && price) {
      current.textContent = price;
    }

    if (compare) {
      if (comparePrice) {
        compare.textContent = comparePrice;
        compare.hidden = false;
      } else {
        compare.textContent = '';
        compare.hidden = true;
      }
    }
  }

  handleSelectionChange() {
    this.syncSelectionFromActiveWrapper();
    this.syncWidgetPriceFromSelection();
    this.syncAtcPriceFromSelection();
  }

  onVariantUpdate(event) {
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;

    this.setActiveWrapper(variantId);
    this.syncVisibility();
    this.syncSelectionFromActiveWrapper();
    this.syncWidgetPriceFromSelection();
    this.syncAtcPriceFromSelection();
  }
}

if (!customElements.get('custom-subscription')) {
  customElements.define('custom-subscription', CustomSubscription);
}
